/**
 * TanStack Query hooks for `cpalead-integration`. Thin wrappers over ./api.
 */
import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";
import {
	createCredential,
	createOffer,
	getStatus,
	listCredentials,
	listOffers,
	listPostbacks,
	cpaleadIntegrationQueryKeys,
	type CreateCredentialBody,
	type CreateOfferBody,
	type CredentialRow,
	type ListCredentialsResponse,
	type ListOffersResponse,
	type ListPostbacksResponse,
	type OfferMapRow,
	type SyncStatusResponse,
} from "./api";

export function useCpaleadCredentialsList(): UseQueryResult<
	ListCredentialsResponse,
	Error
> {
	return useQuery({
		queryKey: cpaleadIntegrationQueryKeys.credentials(),
		queryFn: listCredentials,
	});
}

export function useCpaleadOffersList(): UseQueryResult<ListOffersResponse, Error> {
	return useQuery({
		queryKey: cpaleadIntegrationQueryKeys.offers(),
		queryFn: listOffers,
	});
}

export function useCpaleadPostbacksList(): UseQueryResult<
	ListPostbacksResponse,
	Error
> {
	return useQuery({
		queryKey: cpaleadIntegrationQueryKeys.postbacks(),
		queryFn: listPostbacks,
	});
}

export function useCpaleadSyncStatus(): UseQueryResult<SyncStatusResponse, Error> {
	return useQuery({
		queryKey: cpaleadIntegrationQueryKeys.status(),
		queryFn: getStatus,
	});
}

export function useCpaleadCredentialCreate(): UseMutationResult<
	{ ok: boolean; item: CredentialRow },
	Error,
	CreateCredentialBody
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: createCredential,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: cpaleadIntegrationQueryKeys.all });
		},
	});
}

export function useCpaleadOfferCreate(): UseMutationResult<
	{ ok: boolean; item: OfferMapRow },
	Error,
	CreateOfferBody
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: createOffer,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: cpaleadIntegrationQueryKeys.all });
		},
	});
}
